/**
 * Component: Carousels
 * Description:
 *   Swiper carousel with optional centered / looping behaviour.
 *
 * Usage:
 *   - Automatically initialises on page load.
 *   - Looping is enabled only when there are enough slides.
 *
 * Notes:
 *   - Centered carousels require explicit slide widths in CSS.
 */

import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

import 'swiper/css';

export function carousels(els) {
    if (!els) return;

    els.forEach((carousel) => {
        const carouselEl = carousel.querySelector('.swiper');
        if (!carouselEl) return;

        let mobileDisplay = 1.1;
        let displayCount = 1;
        let destroyAbove = null;
        let isCentered = false;
        let swiperInstance = null;
        let isLooping = false;

        if (carouselEl.hasAttribute('data-js-mobile-display')) {
            mobileDisplay = parseFloat(
                carouselEl.getAttribute('data-js-mobile-display')
            ) || 1;
        }

        if (carouselEl.hasAttribute('data-js-slider-display')) {
            displayCount = parseFloat(
                carouselEl.getAttribute('data-js-slider-display')
            ) || 1;
        }

        if (carouselEl.hasAttribute('data-js-destroy-above')) {
            destroyAbove = parseInt(
                carouselEl.getAttribute('data-js-destroy-above'),
                10
            );
        }

        if (carouselEl.hasAttribute('data-js-slider-centered')) {
            isCentered = true;
            carouselEl.classList.add('is-centered');
        }

        if (carouselEl.hasAttribute('data-js-slider-loop')) {
            isLooping = true;
        }

        /**
         * Accessibility module
         */
        function Accessibility({ on }) {
            on('init', ({ slides }) => {
                if (!slides || !slides.length) return;

                const slideLength = slides.length;
                slides.forEach((slide, index) => {
                    slide.setAttribute('role', 'group');
                    slide.setAttribute('aria-roledescription', 'slide');
                    slide.setAttribute(
                        'aria-label',
                        `Slide ${index + 1} of ${slideLength}`
                    );
                });
            });
        }

        function removeAccessibilityAttributes() {
            const slides = carouselEl.querySelectorAll('.swiper-slide');
            slides.forEach((slide) => {
                slide.removeAttribute('role');
                slide.removeAttribute('aria-roledescription');
                slide.removeAttribute('aria-label');
            });
        }

        function initSwiper() {
            const shouldInit =
                destroyAbove === null || window.innerWidth <= destroyAbove;

            if (!shouldInit && swiperInstance) {
                swiperInstance.destroy(true, true);
                swiperInstance = null;
                removeAccessibilityAttributes();
                carousel.classList.remove('swiper-initialized');
                carousel.classList.add('swiper-destroy');
                return;
            }

            if (!shouldInit || swiperInstance) return;

            const slideCount =
                carouselEl.querySelectorAll('.swiper-slide').length;

            const swiperOptions = {
                slidesPerView: mobileDisplay,
                centeredSlides: false,
                loop: isLooping,
                spaceBetween: 16,
                autoHeight: true,
                effect: "fade",

                navigation: {
                    nextEl: carousel.querySelector('.js-swiper-next'),
                    prevEl: carousel.querySelector('.js-swiper-previous')
                },

                pagination: {
                    el: carousel.querySelector('.swiper-pagination'),
                    clickable: true
                },

                breakpoints: {
                    1135: {
                        slidesPerView: isCentered ? 1.25 : displayCount,
                        spaceBetween: 24,
                        centeredSlides: isCentered,
                        loop: isLooping,
                    },
                    600: {
                        slidesPerView: isCentered ? 1.125 : displayCount,
                        spaceBetween: 24,
                        centeredSlides: isCentered,
                        loop: isLooping,
                    }
                },

                modules: [Navigation, Pagination, Accessibility]
            };

            carousel.classList.remove('swiper-destroy');
            carousel.classList.add('swiper-initialized');

            swiperInstance = new Swiper(carouselEl, swiperOptions);
        }

        initSwiper();

        const onResize = debounce(() => {
            initSwiper();
        }, 200);

        window.addEventListener('resize', onResize);
    });
}

/**
 * Debounce utility
 */
function debounce(func, delay) {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), delay);
    };
}
